import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { vendorApi } from '../../services/api';
import { applyTenantBranding } from '../../utils/portal';
import { clearVendorSession, hasActiveSession } from '../../utils/session';
import { ArrowLeft, ArrowRight, Building2 } from 'lucide-react';
import BrandLogo from '../../components/BrandLogo';

interface LoginOption {
  tenantId: number;
  vendorNo: string;
  companyName: string;
}

export default function VendorLogin() {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [options, setOptions] = useState<LoginOption[] | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already logged in with active session
    if (hasActiveSession()) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const submit = async (selection?: LoginOption) => {
    setError('');
    setLoading(true);

    try {
      const response = await vendorApi.login(
        login,
        password,
        selection ? { tenantId: selection.tenantId, vendorNo: selection.vendorNo } : undefined
      );

      // Same login exists at several companies/vendors - let the user pick
      if (response.data.needsSelection) {
        setOptions(response.data.options);
        return;
      }

      // Verify session was properly established with credentialId
      if (!response.data.vendor?.credentialId) {
        setError('Inloggningen lyckades men sessionen kunde inte skapas. Försök igen.');
        clearVendorSession();
        return;
      }

      // Apply tenant branding from login response (resets it when the tenant has no colour)
      applyTenantBranding(response.data.tenant?.primaryColor);

      // Navigate to dashboard - session is now established in sessionStorage
      navigate('/dashboard');
    } catch (err: any) {
      const status = err.response?.status;
      const message = err.response?.data?.message || 'Inloggningen misslyckades';

      if (status === 401) {
        setError('Felaktigt användarnamn eller lösenord');
      } else if (status === 403) {
        setError('Ditt konto är spärrat eller inaktivt. Kontakta support.');
      } else if (status === 400) {
        setError('Ange användarnamn och lösenord.');
      } else {
        setError(message);
      }

      setOptions(null);
      clearVendorSession();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit();
  };

  const inputClass =
    'w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-[15px] text-ink placeholder-gray-400 transition-shadow duration-300 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/15';

  return (
    <main className="grid min-h-[100dvh] grid-rows-[auto_1fr] bg-gray-50 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:grid-rows-none">
      {/* Brand panel */}
      <section className="bg-grad-deep relative flex flex-col justify-between overflow-hidden px-6 py-8 text-white sm:px-10 lg:px-16 lg:py-14">
        <BrandLogo tone="light" size="md" />
        <div className="hidden lg:block">
          <h1 className="font-display max-w-[16ch] text-balance text-4xl leading-[1.15] xl:text-5xl">
            Avräkningar, fakturor och resurser på ett ställe.
          </h1>
          <p className="mt-6 max-w-[46ch] text-pretty text-lg leading-relaxed text-white/75">
            Följ era uppdrag, avräkningar och utbetalningar direkt från Business Central – utan att vänta på ett mejl.
          </p>
        </div>
        <p className="hidden text-sm text-white/55 lg:block">Settled Portal · Flank Speed Consulting</p>
      </section>

      {/* Sign-in */}
      <section className="flex items-center justify-center px-4 py-12 sm:px-8">
        <div className="w-full max-w-[26rem]">
          <p className="inline-flex items-center gap-3 text-[12px] font-medium uppercase tracking-[0.2em] text-brass">
            <span aria-hidden className="bg-grad-line h-[2px] w-8" />
            Leverantörsportal
          </p>
          <h2 className="font-display mt-4 text-2xl leading-tight text-ink sm:text-3xl">
            {options ? 'Välj företag' : 'Logga in'}
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-muted">
            {options
              ? 'Ditt konto finns hos flera företag. Välj vilket du vill logga in hos.'
              : 'Använd användarnamnet eller e-postadressen du fått från din åkericentral.'}
          </p>

          <div className="panel mt-8 p-6 sm:p-8">
            {error && (
              <p role="alert" className="mb-6 border-l-2 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-800">
                {error}
              </p>
            )}

            {options ? (
              <div className="space-y-2">
                {options.map((option) => (
                  <button
                    key={`${option.tenantId}/${option.vendorNo}`}
                    type="button"
                    disabled={loading}
                    onClick={() => submit(option)}
                    className="group flex w-full items-center gap-4 border border-gray-200 bg-white px-4 py-3.5 text-left transition-all duration-300 ease-fluid hover:border-blue-500 hover:bg-blue-50/60 active:scale-[0.99] disabled:opacity-50"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors group-hover:bg-white group-hover:text-blue-600">
                      <Building2 className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-ink">{option.companyName}</span>
                      <span className="block text-sm text-muted">Leverantör {option.vendorNo}</span>
                    </span>
                    <ArrowRight className="h-4 w-4 text-gray-400 transition-transform duration-300 ease-fluid group-hover:translate-x-0.5 group-hover:text-blue-600" aria-hidden />
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setOptions(null)}
                  className="inline-flex items-center gap-2 pt-3 text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  Tillbaka
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="login" className="mb-2 block text-sm font-medium text-gray-700">
                    Användarnamn eller e-post
                  </label>
                  <input
                    id="login"
                    type="text"
                    autoComplete="username"
                    value={login}
                    onChange={(e) => setLogin(e.target.value)}
                    className={inputClass}
                    placeholder="t.ex. anna@akeri.se"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="password" className="mb-2 block text-sm font-medium text-gray-700">
                    Lösenord
                  </label>
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClass}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="bg-grad-btn group flex w-full items-center justify-between gap-3 rounded-full py-1.5 pl-6 pr-1.5 text-[15px] font-medium text-navy shadow-glow transition-all duration-500 ease-fluid hover:shadow-[0_16px_36px_-12px_rgb(211_167_59_/_0.6)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span>{loading ? 'Loggar in…' : 'Logga in'}</span>
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-navy/10 transition-transform duration-500 ease-fluid group-hover:translate-x-0.5">
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </span>
                </button>
              </form>
            )}
          </div>

          <p className="mt-6 text-sm leading-relaxed text-muted">
            Saknar du inloggning? Kontakta din åkericentral eller{' '}
            <span className="font-medium text-ink">Flank Speed Consulting</span>.
          </p>
        </div>
      </section>
    </main>
  );
}
